/**
 * Dashboard badges + the fill bar (task T.C2).
 *
 * Thin renderers over the pure state in `./status` — every decision (which
 * buckets exist, which tone, what the tooltip says) is data, so the unit suite
 * can pin it down without a DOM.
 */

import { Badge } from '../../components/ui';
import type { DashboardStatus, StatusCountMap } from '../../api/dashboard';

import { MISSING, formatPercent } from './format';
import { barWidth } from './dashboard-model';
import { allBadges, badgeState, nonzeroBadges } from './status';

interface StatusBadgeProps {
  status: DashboardStatus;
  count: number | null | undefined;
  /** Compact = a venue row: the bare count (the six cards above are the legend). */
  compact?: boolean;
  /** Test hook. The campaign cards are unique in the document; table rows are not. */
  testId?: string;
}

/** One status bucket as a coloured pill. `data-status` is the stable hook. */
export function StatusBadge({ status, count, compact = false, testId }: StatusBadgeProps) {
  const state = badgeState(status, count, { compact });
  if (state.hidden) return null;
  return (
    <span className="status-badge" data-status={status} data-testid={testId}>
      <Badge tone={state.tone} title={state.title}>
        {state.text}
      </Badge>
    </span>
  );
}

/**
 * A `byStatus` map as badges.
 *
 * `compact` (venue rows) hides the empty buckets — a venue that has seen no
 * rejections does not print `0 Отказ` six times over. The summary variant
 * (campaign cards) prints all six, zeros included: a campaign card reading `0`
 * is information. An all-zero compact cell renders the absence marker rather
 * than an empty gap.
 */
export function StatusBadges({
  byStatus,
  compact = false,
  testIdPrefix,
}: {
  byStatus: Partial<StatusCountMap> | null | undefined;
  compact?: boolean;
  /** `data-testid` prefix; the status key is appended (`<prefix>-<STATUS>`). */
  testIdPrefix?: string;
}) {
  const badges = compact ? nonzeroBadges(byStatus) : allBadges(byStatus);

  if (badges.length === 0) {
    return (
      <span className="cell-muted" data-testid={testIdPrefix ? `${testIdPrefix}-none` : undefined}>
        {MISSING}
      </span>
    );
  }

  return (
    <div className={compact ? 'status-badges status-badges--compact' : 'status-badges'}>
      {badges.map((badge) => (
        <StatusBadge
          key={badge.status}
          status={badge.status}
          count={badge.count}
          compact={compact}
          testId={testIdPrefix ? `${testIdPrefix}-${badge.status}` : undefined}
        />
      ))}
    </div>
  );
}

/**
 * `fillPercent` as a track + bar. The bar is clamped to the track, the printed
 * value is not — an over-subscribed venue reads `120%` with a full bar. The
 * bar carries the tenth-of-a-percent precision, the label is the whole-number
 * summary (`formatPercent`).
 */
export function FillBar({ percent, label }: { percent: number; label: string }) {
  const width = barWidth(percent);
  return (
    <div
      className="fill"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={width}
      data-testid="fill"
    >
      <span className="fill__track" aria-hidden="true">
        <span className="fill__bar" style={{ width: `${width}%` }} data-fill={width} />
      </span>
      <span className="fill__value">{formatPercent(percent)}</span>
    </div>
  );
}
